<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    modelValue: number;
    min?: number;
    max?: number;
    step?: number;
  }>(),
  {
    min: 0,
    max: 100,
    step: 1,
  }
);

const emit = defineEmits<(e: "update:modelValue", event: Event) => void>();

function onInput(e: Event) {
  emit("update:modelValue", e);
}

const colorMode = useColorMode();
const isDark = computed(() => colorMode.value === "dark");

const progressPercent = computed(() => {
  const { min, max } = props;
  const clampedMax = Math.max(max, min + 1);
  const clampedValue = Math.min(clampedMax, Math.max(min, props.modelValue));
  const p = Math.round(((clampedValue - min) / (clampedMax - min)) * 100);
  return `${p}%`;
});

const THEME_COLORS = {
  dark: {
    fill: "#ffffff",
    empty: "rgba(255, 255, 255, 0.35)",
    thumb: "#ffffff",
  },
  light: {
    fill: "#000000",
    empty: "rgba(0, 0, 0, 0.2)",
    thumb: "#000000",
  },
} as const;

// Dynamic styles based on theme and progress
const rangeStyles = computed(() => {
  const colors = THEME_COLORS[isDark.value ? "dark" : "light"];

  return {
    "--percent": progressPercent.value,
    "--fill-color": colors.fill,
    "--empty-color": colors.empty,
    "--thumb-color": colors.thumb,
  };
});
</script>

<template>
  <div class="w-full">
    <input
      type="range"
      :min="min"
      :max="max"
      :step="step"
      :value="modelValue"
      class="u-range-slider-input"
      :style="rangeStyles"
      @input="onInput"
    >
  </div>
</template>

<style scoped>
.u-range-slider-input {
  @apply w-full cursor-pointer;
  width: 100%;
  height: 0.15rem;
  border-radius: 624.9375rem;
  background: linear-gradient(
    to right,
    var(--fill-color) 0%,
    var(--fill-color) var(--percent),
    var(--empty-color) var(--percent),
    var(--empty-color) 100%
  );
  -webkit-appearance: none;
  appearance: none;
}

.u-range-slider-input:focus {
  outline: none;
}

/* WebKit */
.u-range-slider-input::-webkit-slider-runnable-track {
  height: 0.15rem;
  background: transparent;
}
.u-range-slider-input::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 624.9375rem;
  background: var(--thumb-color);
  border: none;
  box-shadow: 0 0.0625rem 0.125rem rgba(5, 27, 68, 0.24);
  margin-top: -0.25rem; /* center thumb on 0.25rem track */
  cursor: pointer;
}

/* Firefox */
.u-range-slider-input::-moz-range-track {
  height: 0.25rem;
  background: transparent;
}
.u-range-slider-input::-moz-range-progress {
  height: 0.25rem;
  background: var(--fill-color);
}
.u-range-slider-input::-moz-range-thumb {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 624.9375rem;
  background: var(--thumb-color);
  border: none;
  box-shadow: 0 0.0625rem 0.125rem rgba(5, 27, 68, 0.24);
  cursor: pointer;
}

/* Edge/IE (legacy) */
.u-range-slider-input::-ms-track {
  height: 0.25rem;
  background: transparent;
  border-color: transparent;
  color: transparent;
}
.u-range-slider-input::-ms-fill-lower {
  background: var(--fill-color);
}
.u-range-slider-input::-ms-fill-upper {
  background: var(--empty-color);
}
.u-range-slider-input::-ms-thumb {
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 624.9375rem;
  background: var(--thumb-color);
  border: none;
  box-shadow: 0 0.0625rem 0.125rem rgba(5, 27, 68, 0.24);
  cursor: pointer;
}
</style>
